import * as React from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  Stack,
} from '@mui/material';
import { zodResolver } from '@hookform/resolvers/zod';
import { Controller, useForm } from 'react-hook-form';
import { z as zod } from 'zod';
import { useMutation, useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';

import { addUserToCommunity } from '@/api/accounts';
import { getCommunities } from '@/api/communities';
import useUserTitles from '@/hooks/use-user-titles';

const schema = zod.object({
  community_id: zod.string().min(1, { message: 'Community is required' }),
  role_name: zod.coerce.string().min(1, { message: 'Role is required' }),
  user_title_id: zod.string().optional(),
});

type Values = zod.infer<typeof schema>;

interface AddCommunityModalProps {
  accountId: string;
  userId: string;
  existingCommunityIds: string[];
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const communityRoles = [
  { value: 'community_manager', label: 'Community Manager' },
  { value: 'board_member', label: 'Board Member' },
  { value: 'community_member', label: 'Community Member' },
];

export function AddCommunityModal({
  accountId,
  userId,
  existingCommunityIds,
  open,
  onClose,
  onSuccess,
}: AddCommunityModalProps): React.JSX.Element {
  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<Values>({
    resolver: zodResolver(schema),
    defaultValues: {
      community_id: '',
      role_name: '',
      user_title_id: '',
    },
  });

  const { userTitles } = useUserTitles();

  const { data: communitiesData } = useQuery({
    queryKey: ['communities'],
    queryFn: getCommunities,
  });

  // Filter out communities that the user is already assigned to.
  // We compare against BOTH the numeric id (stringified) and the uuid because
  // upstream consumers may pass either shape, and the dropdown's value is the
  // uuid when available.
  const availableCommunities = React.useMemo(() => {
    if (!communitiesData?.communities) return [];
    const taken = new Set(existingCommunityIds.map((id) => String(id)));
    return communitiesData.communities.filter((community) => {
      const candidates = [
        community.uuid,
        community.id != null ? String(community.id) : null,
      ].filter(Boolean) as string[];
      return !candidates.some((c) => taken.has(c));
    });
  }, [communitiesData, existingCommunityIds]);

  const mutation = useMutation({
    mutationFn: (data: Values) => addUserToCommunity(accountId, userId, data),
    onSuccess: () => {
      toast.success('User added to community successfully');
      reset();
      onSuccess();
      onClose();
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to add user to community');
    },
  });

  const onSubmit = React.useCallback(
    async (values: Values): Promise<void> => {
      mutation.mutate({
        community_id: values.community_id,
        role_name: values.role_name,
        user_title_id: values.user_title_id || undefined,
      });
    },
    [mutation]
  );

  // Reset form state whenever the modal is closed so stale selections don't persist.
  React.useEffect(() => {
    if (!open) {
      reset({ community_id: '', role_name: '', user_title_id: '' });
    }
  }, [open, reset]);

  const noAvailableCommunities = availableCommunities.length === 0;

  return (
    <Dialog
      fullWidth
      maxWidth="sm"
      onClose={onClose}
      open={open}
      PaperProps={{
        component: 'form',
        onSubmit: handleSubmit(onSubmit),
      }}
    >
      <DialogTitle>Add User to Community</DialogTitle>
      <DialogContent>
        <Stack spacing={3} sx={{ mt: 1 }}>
          <Controller
            control={control}
            name="community_id"
            render={({ field }) => (
              <FormControl error={Boolean(errors.community_id)} fullWidth>
                <InputLabel>Community</InputLabel>
                <Select
                  {...field}
                  label="Community"
                  disabled={noAvailableCommunities}
                >
                  <MenuItem value="">Select a community</MenuItem>
                  {availableCommunities.map((community) => (
                    <MenuItem
                      key={community.uuid ?? community.id}
                      value={community.uuid ?? String(community.id)}
                    >
                      {community.name}
                    </MenuItem>
                  ))}
                </Select>
                {noAvailableCommunities ? (
                  <FormHelperText>
                    This user is already assigned to every community.
                  </FormHelperText>
                ) : errors.community_id ? (
                  <FormHelperText>{errors.community_id.message}</FormHelperText>
                ) : null}
              </FormControl>
            )}
          />

          <Controller
            control={control}
            name="role_name"
            render={({ field }) => (
              <FormControl error={Boolean(errors.role_name)} fullWidth>
                <InputLabel>Role</InputLabel>
                <Select {...field} label="Role">
                  {communityRoles.map((role) => (
                    <MenuItem key={role.value} value={role.value}>
                      {role.label}
                    </MenuItem>
                  ))}
                </Select>
                {errors.role_name && (
                  <FormHelperText>{errors.role_name.message}</FormHelperText>
                )}
              </FormControl>
            )}
          />

          <Controller
            control={control}
            name="user_title_id"
            render={({ field }) => (
              <FormControl fullWidth>
                <InputLabel>Title (optional)</InputLabel>
                <Select {...field} label="Title (optional)">
                  <MenuItem value="">None</MenuItem>
                  {(userTitles ?? []).map((title) => (
                    <MenuItem key={title.id} value={String(title.id)}>
                      {title.title}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          disabled={mutation.isPending || noAvailableCommunities}
          type="submit"
          variant="contained"
        >
          {mutation.isPending ? 'Adding...' : 'Add to Community'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
